import { type GetResourceAgendaTeamScheduleResponse } from "@swivu/client-api";
import { groupBy } from "@swivu/shared-ui";
import { densePacking } from "./densePacking.js";
import { isDateInRange } from "./utils/isInDateRange.js";

export interface TeamScheduleRow {
  resourceId: number;
  rowLabel: string;
  rowLink?: string;
  dates: {
    dateLink: string;
    resourceId: number;
    start_time: string;
    end_time: string;
    color: string;
    label: string;
    style?: string | undefined | null;
    viewTransitions?: {
      background?: string;
      title?: string;
    };
    row: number;
  }[];
}

export type TeamScheduleAsGroup = GetResourceAgendaTeamScheduleResponse;
export interface TeamScheduleResouce {
  id: number;
  board: number;
  display_name: string;
  tasks: Task[];
  label?: string;
}

interface Task {
  start_time: string;
  end_time: string;
  display_name: string;
  color: string;
  style?: string;
  id: number;
}

type GroupByStrategiesOptions = {
  isPublic: boolean | undefined;
  slug: string | undefined;
};

const GroupByStrategies = {
  oldresource: (
    teamSchedule: TeamScheduleAsGroup,
    options: GroupByStrategiesOptions
  ) => {
    return teamSchedule.map((resource) => {
      const rowLink = options?.isPublic
        ? `/schedule/${options.slug ?? ""}/resource/${resource.id}?titles=${
            resource.display_name
          }`
        : `/resources?ids=${resource.id}&titles=${resource.display_name}`;

      return {
        resourceId: resource.id,
        rowLabel:
          resource.label || resource.display_name || "No resource label",
        rowLink,
        dates: resource.tasks.reduce((acc: TeamScheduleRow["dates"], task) => {
          const params = new URLSearchParams();
          params.append("ids", task.id.toString());
          params.append("colors", task.color);
          params.append("titles", task.display_name);

          const dateLink = options?.isPublic
            ? `/schedule/${options.slug ?? ""}/task/${task.id}`
            : `/tasks?${params.toString()}`;

          const hasPreviousStartTime = acc.find(
            (t) => t.start_time === task.start_time
          );
          if (
            hasPreviousStartTime &&
            hasPreviousStartTime.end_time === task.end_time
          ) {
            // this doesn't work if there are two tasks with the exact same time
            // return acc;
          }
          acc.push({
            dateLink,
            resourceId: resource.id,
            start_time: task.start_time,
            end_time: task.end_time,
            color: task.color,
            label: task.display_name,
            style: task?.style,
            viewTransitions: {
              background: `task-background-${task.id}`,
              title: `task-title-${task.id}`
            },
            row: 0 // Initialize with default row 0
          });
          return acc;
        }, [])
      };
    });
  },
  resource: (
    teamSchedule: TeamScheduleAsGroup,
    options: GroupByStrategiesOptions,
    _dateRange: Date[],
    timeZone: string
  ) => {
    // const result = [bruceTest, mTest].map((resource) => {
    const result = teamSchedule.map((resource) => {
      const rowLink = options?.isPublic
        ? `/schedule/${options.slug ?? ""}/resource/${resource.id}?titles=${
            resource.display_name
          }`
        : `/resources?ids=${resource.id}&titles=${resource.display_name}`;

      return {
        resourceId: resource.id,
        rowLabel:
          resource.label || resource.display_name || "No resource label",
        rowLink,
        dates: resource.tasks.reduce((acc: TeamScheduleRow["dates"], task) => {
          const params = new URLSearchParams();
          params.append("ids", task.id.toString());
          params.append("colors", task.color);
          params.append("titles", task.display_name);

          const dateLink = options?.isPublic
            ? `/schedule/${options.slug ?? ""}/task/${task.id}`
            : `/tasks?${params.toString()}`;

          const hasPreviousStartTime = acc.find(
            (t) => t.start_time === task.start_time
          );
          if (
            hasPreviousStartTime &&
            hasPreviousStartTime.end_time === task.end_time
          ) {
            // this doesn't work if there are two tasks with the exact same time
            // return acc;
          }
          acc.push({
            dateLink,
            resourceId: resource.id,
            start_time: task.start_time,
            end_time: task.end_time,
            color: task.color,
            label: task.display_name,
            style: task?.style,
            viewTransitions: {
              background: `task-background-${task.id}`,
              title: `task-title-${task.id}`
            },
            row: 0 // Initialize with default row 0
          });
          return acc;
        }, [])
      };
    });
    const densePacked = densePacking(result, timeZone);
    // console.log("densePacked", densePacked);
    return densePacked;
  },
  task: (
    teamSchedule: TeamScheduleAsGroup,
    options: GroupByStrategiesOptions,
    _dateRange: Date[],
    timeZone: string
  ) => {
    // alert("task");
    const flattenTasks = teamSchedule.reduce(
      (acc: TeamScheduleRow[], resource: TeamScheduleAsGroup[number]) => {
        resource.tasks.forEach((task) => {
          const params = new URLSearchParams();
          params.append("ids", task.id.toString());
          params.append("color", task.color);
          params.append("titles", task.display_name);
          const rowLink = options?.isPublic
            ? `/schedule/${options.slug ?? ""}/task/${task.id}`
            : `/tasks?${params.toString()}`;
          const dateLink = options?.isPublic
            ? `/schedule/${options.slug ?? ""}/resource/${resource.id}`
            : `/resources?ids=${resource.id}&titles=${resource.display_name}`;
          acc.push({
            resourceId: task.id,
            rowLabel: task?.display_name ?? "No task label",
            rowLink,
            dates: [
              {
                dateLink,
                resourceId: resource.id,
                start_time: task.start_time,
                end_time: task.end_time,
                color: task.color,
                label: resource.display_name || resource?.label || "",
                viewTransitions: {
                  background: `task-background-${task.id}`,
                  title: `task-title-${task.id}`
                },
                style: task?.style,
                row: 0 // Initialize with default row 0, densePacking will update this
              }
            ]
          });
        });
        return acc;
      },
      []
    );
    const groupedTasks = groupBy(flattenTasks, "rowLabel");
    const formatGroupedTasks = Object.entries(groupedTasks).map(
      ([key, value]) => {
        const dates: TeamScheduleRow["dates"] = [];
        value.forEach((task) => {
          task.dates.forEach((date) => {
            dates.push({
              dateLink: date.dateLink,
              resourceId: task?.resourceId,
              start_time: date.start_time,
              end_time: date.end_time,
              color: date.color,
              label: date.label,
              style: date?.style,
              viewTransitions: date.viewTransitions,
              row: 0 // Initialize with default row 0, densePacking will update this
            });
          });
          return dates;
        });
        return {
          rowLink: value[0].rowLink,
          rowLabel: key,
          resourceId: value[0].resourceId,
          dates: dates
        };
      }
    );

    const removeDuplicates = formatGroupedTasks.map((resource) => {
      const seen = new Set();
      return {
        ...resource,
        dates: resource.dates.filter((date) => {
          const key = `${date.start_time}-${date.end_time}-${date.label}`;
          if (seen.has(key)) {
            return false;
          }
          seen.add(key);
          return true;
        })
      };
    });
    const packed: TeamScheduleRow[] = densePacking(removeDuplicates, timeZone);
    return packed;
  }
};

export type GroupByStrategiesKeys = keyof typeof GroupByStrategies;

export type GridData = ReturnType<ReturnType<typeof groupTeamSchedule>>;

const groupTeamSchedule =
  <T extends GroupByStrategiesKeys>(
    groupBy: T,
    dateRange: Date[],
    timeZone: string,
    options: GroupByStrategiesOptions
  ) =>
  (team: TeamScheduleAsGroup) => {
    // how many keys are in an object?
    const filteredForNoTasks = team
      .map((resource) => {
        return {
          ...resource,
          // turn object into array
          tasks: Object.values(resource.tasks)
        };
      })
      .filter((resource) => Object.keys(resource?.tasks ?? {}).length);

    const filteredForDateRange = isDateInRange(
      dateRange,
      filteredForNoTasks,
      timeZone
    ).filter((resource) => resource.tasks.length);

    const result = GroupByStrategies[groupBy](
      filteredForDateRange,
      options,
      dateRange,
      timeZone
    );
    return result;
  };

export type GroupTeamSchedule<T extends GroupByStrategiesKeys> = ReturnType<
  ReturnType<typeof groupTeamSchedule<T>>
>;
export default groupTeamSchedule;

export interface Root {
  resource: Resource;
  dateRange: string[];
}

export interface Resource {
  resourceId: number;
  rowLabel: string;
  rowLink: string;
  dates: {
    dateLink: string;
    resourceId: number;
    start_time: string;
    end_time: string;
    color: string;
    label: string;
    style?: string;
    viewTransitions?: {
      background?: string;
      title?: string;
    };
  }[];
}
